import { Box3, CatmullRomCurve3, MathUtils, Vector3 } from 'three';
import type { TrackControlPoint, TrackDefinition } from '../core/types';

export type BarrierSide = 'left' | 'right';
export type BarrierState = NonNullable<TrackControlPoint['barriers']>;
export type TrackMarkerKind = 'checkpoint' | 'booster';

export interface TrackSample {
  progress: number;
  distance: number;
  sampleIndex: number;
  position: Vector3;
  forward: Vector3;
  right: Vector3;
  up: Vector3;
  /** Alias retained for geometry builders that use curve terminology. */
  tangent: Vector3;
  /** Alias retained for road mesh builders that use cross-track terminology. */
  side: Vector3;
  width: number;
  /** Road roll in radians. */
  bank: number;
  barriers: BarrierState;
  /** False inside an authored air gap. */
  driveable: boolean;
  /** True only on a narrow launch lip and the first metres of its gap. */
  launch: boolean;
  /** Ballistic tuning for the active launch, or null away from a jump. */
  jump: TrackJumpRange | null;
}

export interface TrackJumpRange {
  id: string;
  index: number;
  /** Authored jumpFraction and the first non-driveable point. */
  progress: number;
  /** Start of the narrow, still-driveable launch lip. */
  launchStart: number;
  /** Start (inclusive) and end (exclusive) of the air gap. */
  gapStart: number;
  gapEnd: number;
  /** A driveable point shortly beyond the gap used to tune the flight arc. */
  landingProgress: number;
  lipLength: number;
  gapLength: number;
  landingBuffer: number;
  launchImpulse: number;
  minimumLaunchSpeed: number;
  landingHeight: number;
  landingHeightDelta: number;
}

export interface TrackTransform {
  progress: number;
  position: Vector3;
  forward: Vector3;
  right: Vector3;
  up: Vector3;
  yaw: number;
  pitch: number;
  roll: number;
}

export interface TrackMarker {
  id: string;
  kind: TrackMarkerKind;
  index: number;
  progress: number;
  distance: number;
  position: Vector3;
  forward: Vector3;
  right: Vector3;
  up: Vector3;
  width: number;
  triggerRadius: number;
  resetTransform: TrackTransform;
}

export interface TrackProjection {
  progress: number;
  /** Distance from the start along the centerline, in metres. */
  distance: number;
  distanceToCenter: number;
  spatialDistanceToCenter: number;
  lateralOffset: number;
  verticalOffset: number;
  onRoad: boolean;
  center: Vector3;
  sample: TrackSample;
}

export interface TrackRuntimeOptions {
  /** Number of cached centerline segments used for projection and geometry. */
  sampleCount?: number;
  /** Vertical clearance used by generated reset transforms. */
  resetHeight?: number;
}

export interface TrackGeometryMetrics {
  totalLength: number;
  minControlPointDistance: number;
  maxControlPointDistance: number;
  elevationGain: number;
  elevationLoss: number;
  bounds: Box3;
}

export interface TrackValidationIssue {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  index?: number;
}

export interface TrackValidationResult {
  valid: boolean;
  issues: TrackValidationIssue[];
  errors: TrackValidationIssue[];
  warnings: TrackValidationIssue[];
  metrics: TrackGeometryMetrics | null;
}

export const MIN_CONTROL_POINT_SPACING = 1;
export const MAX_CONTROL_POINT_SPACING = 260;
export const MIN_TRACK_LENGTH = 600;

const WORLD_UP = new Vector3(0, 1, 0);
const DEFAULT_SAMPLE_COUNT = 1_536;
const DEFAULT_RESET_HEIGHT = 1.05;
const RESET_FORWARD_OFFSET = 2.5;
const ROAD_VERTICAL_TOLERANCE = 3.5;
const EPSILON = 1e-8;
const LAUNCH_CAPTURE_DISTANCE = 4;

interface JumpThemeTuning {
  lipLength: number;
  gapLength: number;
  landingBuffer: number;
  launchImpulse: number;
  minimumLaunchSpeed: number;
}

const JUMP_TUNING_BY_THEME: Readonly<Record<TrackDefinition['theme'], JumpThemeTuning>> = {
  coast: {
    lipLength: 4,
    gapLength: 20,
    landingBuffer: 6,
    launchImpulse: 3.8,
    minimumLaunchSpeed: 38,
  },
  forest: {
    lipLength: 4.25,
    gapLength: 24,
    landingBuffer: 6,
    launchImpulse: 4.2,
    minimumLaunchSpeed: 40,
  },
  neon: {
    lipLength: 4,
    gapLength: 20,
    landingBuffer: 6,
    launchImpulse: 3.8,
    minimumLaunchSpeed: 38,
  },
  desert: {
    lipLength: 4.5,
    gapLength: 28,
    landingBuffer: 7,
    launchImpulse: 4.6,
    minimumLaunchSpeed: 42,
  },
  sky: {
    lipLength: 5,
    gapLength: 32,
    landingBuffer: 8,
    launchImpulse: 5,
    minimumLaunchSpeed: 44,
  },
  gauntlet: {
    lipLength: 5,
    gapLength: 36,
    landingBuffer: 9,
    launchImpulse: 5.4,
    minimumLaunchSpeed: 45,
  },
};

function clampProgress(progress: number): number {
  return MathUtils.clamp(Number.isFinite(progress) ? progress : 0, 0, 1);
}

function inRange(progress: number, range: readonly number[]): boolean {
  return progress >= range[0] && progress <= range[1];
}

function vectorFromPoint(point: TrackControlPoint): Vector3 {
  return new Vector3(point.x, point.y, point.z);
}

function cloneTransform(transform: TrackTransform): TrackTransform {
  return {
    progress: transform.progress,
    position: transform.position.clone(),
    forward: transform.forward.clone(),
    right: transform.right.clone(),
    up: transform.up.clone(),
    yaw: transform.yaw,
    pitch: transform.pitch,
    roll: transform.roll,
  };
}

function createCurve(definition: TrackDefinition, arcLengthDivisions: number): CatmullRomCurve3 {
  const curve = new CatmullRomCurve3(
    definition.points.map(vectorFromPoint),
    false,
    'centripetal',
    0.5,
  );
  curve.arcLengthDivisions = Math.max(200, arcLengthDivisions);
  curve.updateArcLengths();
  return curve;
}

function validateProgressList(
  issues: TrackValidationIssue[],
  values: readonly number[],
  label: string,
): void {
  let previous = -Infinity;
  values.forEach((value, index) => {
    if (!Number.isFinite(value) || value <= 0 || value >= 1) {
      issues.push({
        severity: 'error',
        code: `${label}-progress-out-of-range`,
        message: `${label} ${index + 1} must be strictly between start and finish.`,
        index,
      });
    }
    if (value <= previous) {
      issues.push({
        severity: 'error',
        code: `${label}-progress-order`,
        message: `${label} progress values must be strictly increasing.`,
        index,
      });
    }
    previous = value;
  });
}

function validateRanges(
  issues: TrackValidationIssue[],
  ranges: readonly (readonly number[])[] | undefined,
  label: string,
  expectedLength: number,
): void {
  ranges?.forEach((range, index) => {
    if (
      range.length !== expectedLength ||
      !Number.isFinite(range[0]) ||
      !Number.isFinite(range[1]) ||
      range[0] < 0 ||
      range[1] > 1 ||
      range[0] >= range[1]
    ) {
      issues.push({
        severity: 'error',
        code: `${label}-range-invalid`,
        message: `${label} ${index + 1} must have an ordered range within 0..1.`,
        index,
      });
    }
    if (expectedLength === 3 && !Number.isFinite(range[2])) {
      issues.push({
        severity: 'error',
        code: `${label}-bank-invalid`,
        message: `${label} ${index + 1} must provide a finite bank angle.`,
        index,
      });
    }
  });
}

/** Returns deterministic measurements without constructing a full TrackRuntime. */
export function measureTrackGeometry(definition: TrackDefinition): TrackGeometryMetrics {
  if (definition.points.length < 2) {
    return {
      totalLength: 0,
      minControlPointDistance: 0,
      maxControlPointDistance: 0,
      elevationGain: 0,
      elevationLoss: 0,
      bounds: new Box3(),
    };
  }

  const vectors = definition.points.map(vectorFromPoint);
  let minControlPointDistance = Infinity;
  let maxControlPointDistance = 0;
  let elevationGain = 0;
  let elevationLoss = 0;

  for (let index = 1; index < vectors.length; index += 1) {
    const distance = vectors[index].distanceTo(vectors[index - 1]);
    minControlPointDistance = Math.min(minControlPointDistance, distance);
    maxControlPointDistance = Math.max(maxControlPointDistance, distance);
    const rise = vectors[index].y - vectors[index - 1].y;
    if (rise > 0) elevationGain += rise;
    else elevationLoss += -rise;
  }

  const curve = createCurve(definition, Math.max(512, definition.points.length * 32));
  return {
    totalLength: curve.getLength(),
    minControlPointDistance,
    maxControlPointDistance,
    elevationGain,
    elevationLoss,
    bounds: new Box3().setFromPoints(vectors),
  };
}

export function validateTrackDefinition(definition: TrackDefinition): TrackValidationResult {
  const issues: TrackValidationIssue[] = [];

  if (!definition.id.trim()) {
    issues.push({ severity: 'error', code: 'missing-id', message: 'Track id is required.' });
  }
  if (!definition.name.trim()) {
    issues.push({ severity: 'error', code: 'missing-name', message: 'Track name is required.' });
  }
  if (!Number.isFinite(definition.roadWidth) || definition.roadWidth < 6) {
    issues.push({
      severity: 'error',
      code: 'road-width-invalid',
      message: 'Road width must be at least six metres.',
    });
  }
  if (definition.points.length < 4) {
    issues.push({
      severity: 'error',
      code: 'too-few-control-points',
      message: 'A point-to-point track needs at least four control points.',
    });
  }

  definition.points.forEach((point, index) => {
    if (![point.x, point.y, point.z].every(Number.isFinite)) {
      issues.push({
        severity: 'error',
        code: 'control-point-not-finite',
        message: `Control point ${index + 1} has a non-finite coordinate.`,
        index,
      });
    }
    if (point.width !== undefined && (!Number.isFinite(point.width) || point.width < 6)) {
      issues.push({
        severity: 'error',
        code: 'control-point-width-invalid',
        message: `Control point ${index + 1} has an unsafe road width.`,
        index,
      });
    }
    if (point.bank !== undefined && !Number.isFinite(point.bank)) {
      issues.push({
        severity: 'error',
        code: 'control-point-bank-invalid',
        message: `Control point ${index + 1} has a non-finite bank.`,
        index,
      });
    }
  });

  validateProgressList(issues, definition.checkpointFractions, 'checkpoint');
  validateProgressList(issues, definition.boosterFractions, 'booster');
  validateProgressList(issues, definition.jumpFractions ?? [], 'jump');
  validateRanges(issues, definition.openFractions, 'open', 2);
  validateRanges(issues, definition.tunnelFractions, 'tunnel', 2);
  validateRanges(issues, definition.bankedFractions, 'banked', 3);

  if (definition.checkpointFractions.length < 3) {
    issues.push({
      severity: 'error',
      code: 'too-few-checkpoints',
      message: 'A complete course needs at least three ordered checkpoints.',
    });
  }
  if (definition.boosterFractions.length < 1) {
    issues.push({
      severity: 'error',
      code: 'missing-booster',
      message: 'Every course needs at least one booster.',
    });
  }
  if (
    definition.targetTime.length !== 2 ||
    definition.targetTime[0] <= 0 ||
    definition.targetTime[1] <= definition.targetTime[0]
  ) {
    issues.push({
      severity: 'error',
      code: 'target-time-invalid',
      message: 'Target time must be an increasing pair of positive seconds.',
    });
  }

  let metrics: TrackGeometryMetrics | null = null;
  if (definition.points.length >= 2 && !issues.some((issue) => issue.code === 'control-point-not-finite')) {
    metrics = measureTrackGeometry(definition);
    if (metrics.minControlPointDistance < MIN_CONTROL_POINT_SPACING) {
      issues.push({
        severity: 'error',
        code: 'degenerate-control-point',
        message: `Adjacent control points must be at least ${MIN_CONTROL_POINT_SPACING}m apart.`,
      });
    }
    if (metrics.maxControlPointDistance > MAX_CONTROL_POINT_SPACING) {
      issues.push({
        severity: 'error',
        code: 'disconnected-control-points',
        message: `Adjacent control points may not be more than ${MAX_CONTROL_POINT_SPACING}m apart.`,
      });
    }
    if (metrics.totalLength < MIN_TRACK_LENGTH) {
      issues.push({
        severity: 'error',
        code: 'track-too-short',
        message: `A complete track must be at least ${MIN_TRACK_LENGTH}m long.`,
      });
    }
  }

  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');
  return { valid: errors.length === 0, issues, errors, warnings, metrics };
}

export function assertValidTrackDefinition(definition: TrackDefinition): void {
  const validation = validateTrackDefinition(definition);
  if (!validation.valid) {
    const detail = validation.errors.map((issue) => `${issue.code}: ${issue.message}`).join('\n');
    throw new Error(`Invalid track definition "${definition.id}":\n${detail}`);
  }
}

export class TrackRuntime {
  readonly definition: TrackDefinition;
  readonly curve: CatmullRomCurve3;
  readonly totalLength: number;
  readonly checkpointProgresses: readonly number[];
  readonly boosterProgresses: readonly number[];
  readonly checkpoints: readonly TrackMarker[];
  readonly boosters: readonly TrackMarker[];
  readonly jumpProgresses: readonly number[];
  readonly jumpRanges: readonly TrackJumpRange[];

  private readonly sampleCount: number;
  private readonly resetHeight: number;
  private readonly centerlineCache: readonly Vector3[];
  private readonly controlPointFractions: readonly number[];

  constructor(definition: TrackDefinition, options: TrackRuntimeOptions = {}) {
    assertValidTrackDefinition(definition);
    this.definition = definition;
    this.sampleCount = MathUtils.clamp(
      Math.round(options.sampleCount ?? Math.max(DEFAULT_SAMPLE_COUNT, definition.points.length * 64)),
      128,
      8_192,
    );
    this.resetHeight = Math.max(0.25, options.resetHeight ?? DEFAULT_RESET_HEIGHT);
    this.curve = createCurve(definition, this.sampleCount);
    this.totalLength = this.curve.getLength();
    this.controlPointFractions = this.buildControlPointFractions();
    this.centerlineCache = Array.from({ length: this.sampleCount + 1 }, (_, index) =>
      this.curve.getPointAt(index / this.sampleCount),
    );
    this.checkpointProgresses = Object.freeze([...definition.checkpointFractions]);
    this.boosterProgresses = Object.freeze([...definition.boosterFractions]);
    this.jumpProgresses = Object.freeze(this.getAuthoredJumpProgresses());
    this.jumpRanges = Object.freeze(this.buildJumpRanges());
    this.checkpoints = Object.freeze(
      this.checkpointProgresses.map((progress, index) => this.createMarker('checkpoint', progress, index)),
    );
    this.boosters = Object.freeze(
      this.boosterProgresses.map((progress, index) => this.createMarker('booster', progress, index)),
    );
  }

  sampleAt(progress: number): TrackSample {
    const safeProgress = clampProgress(progress);
    const position = this.curve.getPointAt(safeProgress);
    const forward = this.curve.getTangentAt(safeProgress).normalize();

    let right = new Vector3().crossVectors(WORLD_UP, forward);
    if (right.lengthSq() < EPSILON) right.set(1, 0, 0);
    else right.normalize();

    const bank = this.getBankAt(safeProgress);
    if (Math.abs(bank) > EPSILON) right.applyAxisAngle(forward, bank).normalize();
    const up = new Vector3().crossVectors(forward, right).normalize();
    const barriers = this.getBarrierStateAt(safeProgress);
    const jump = this.getJumpRangeAt(safeProgress);

    return {
      progress: safeProgress,
      distance: safeProgress * this.totalLength,
      sampleIndex: Math.min(this.sampleCount, Math.round(safeProgress * this.sampleCount)),
      position,
      forward,
      right,
      up,
      tangent: forward.clone(),
      side: right.clone(),
      width: this.interpolatePointNumber(safeProgress, 'width', this.definition.roadWidth),
      bank,
      barriers,
      driveable: !this.isGapAt(safeProgress),
      launch: this.isJumpAt(safeProgress),
      jump,
    };
  }

  /** Compatibility alias for systems that use shorter sampling terminology. */
  sample(progress: number): TrackSample {
    return this.sampleAt(progress);
  }

  sampleDistance(distance: number): TrackSample {
    const safeDistance = MathUtils.clamp(Number.isFinite(distance) ? distance : 0, 0, this.totalLength);
    return this.sampleAt(this.totalLength > 0 ? safeDistance / this.totalLength : 0);
  }

  nearest(position: Vector3 | { x: number; y: number; z: number }, hintProgress?: number): TrackProjection {
    const query = position instanceof Vector3 ? position : new Vector3(position.x, position.y, position.z);
    const hintIsValid = hintProgress !== undefined && Number.isFinite(hintProgress);
    let startIndex = 0;
    let endIndex = this.sampleCount - 1;

    if (hintIsValid) {
      const centerIndex = Math.round(clampProgress(hintProgress as number) * this.sampleCount);
      const radius = Math.max(24, Math.ceil(this.sampleCount * 0.055));
      startIndex = Math.max(0, centerIndex - radius);
      endIndex = Math.min(this.sampleCount - 1, centerIndex + radius);
    }

    let candidate = this.nearestCachedSegment(query, startIndex, endIndex);
    const localRoadWidth = this.sampleAt(candidate.progress).width;
    const localCandidate = this.curve.getPointAt(candidate.progress);
    const localPlanarDistance = Math.hypot(query.x - localCandidate.x, query.z - localCandidate.z);
    if (hintIsValid && localPlanarDistance > localRoadWidth * 5) {
      candidate = this.nearestCachedSegment(query, 0, this.sampleCount - 1);
    }

    const refinementRadius = 2 / this.sampleCount;
    let low = Math.max(0, candidate.progress - refinementRadius);
    let high = Math.min(1, candidate.progress + refinementRadius);
    for (let iteration = 0; iteration < 18; iteration += 1) {
      const third = (high - low) / 3;
      const left = low + third;
      const right = high - third;
      const leftDistance = this.curve.getPointAt(left).distanceToSquared(query);
      const rightDistance = this.curve.getPointAt(right).distanceToSquared(query);
      if (leftDistance <= rightDistance) high = right;
      else low = left;
    }

    const progress = (low + high) * 0.5;
    const sample = this.sampleAt(progress);
    const offset = query.clone().sub(sample.position);
    const spatialDistanceToCenter = offset.length();
    const distanceToCenter = Math.hypot(offset.x, offset.z);
    const lateralOffset = offset.dot(sample.right);
    const verticalOffset = offset.dot(sample.up);
    const onRoad =
      sample.driveable &&
      Math.abs(lateralOffset) <= sample.width * 0.5 + 0.5 &&
      Math.abs(verticalOffset) <= ROAD_VERTICAL_TOLERANCE;

    return {
      progress,
      distance: progress * this.totalLength,
      distanceToCenter,
      spatialDistanceToCenter,
      lateralOffset,
      verticalOffset,
      onRoad,
      center: sample.position.clone(),
      sample,
    };
  }

  projectToRoad(
    position: Vector3 | { x: number; y: number; z: number },
    hintProgress?: number,
  ): TrackProjection {
    return this.nearest(position, hintProgress);
  }

  getStartTransform(): TrackTransform {
    return this.transformAt(0, 0);
  }

  getFinishTransform(): TrackTransform {
    return this.transformAt(1, 0);
  }

  getCheckpointTransform(index: number): TrackTransform {
    if (!Number.isInteger(index) || index < 0 || index >= this.checkpointProgresses.length) {
      throw new RangeError(`Checkpoint index ${index} is outside this track.`);
    }
    return this.transformAt(this.checkpointProgresses[index], RESET_FORWARD_OFFSET);
  }

  /**
   * -1 means no checkpoint has been passed and returns the starting transform.
   * Out-of-date progress beyond the final checkpoint safely clamps to the last.
   */
  getSafeResetTransform(lastCheckpointIndex = -1): TrackTransform {
    if (!Number.isFinite(lastCheckpointIndex) || lastCheckpointIndex < 0) return this.getStartTransform();
    const safeIndex = Math.min(Math.floor(lastCheckpointIndex), this.checkpointProgresses.length - 1);
    return this.getCheckpointTransform(safeIndex);
  }

  /** Compatibility alias used by the race reset service. */
  getResetTransform(lastCheckpointIndex = -1): TrackTransform {
    return this.getSafeResetTransform(lastCheckpointIndex);
  }

  isOpenAt(progress: number): boolean {
    const safeProgress = clampProgress(progress);
    return this.definition.openFractions?.some((range) => inRange(safeProgress, range)) ?? false;
  }

  getBarrierStateAt(progress: number): BarrierState {
    const safeProgress = clampProgress(progress);
    if (this.isOpenAt(safeProgress)) return 'none';
    return this.nearestPointValue(safeProgress, 'barriers', 'both');
  }

  hasBarrierAt(progress: number, side: BarrierSide): boolean {
    const barriers = this.getBarrierStateAt(progress);
    return barriers === 'both' || barriers === side;
  }

  isJumpAt(progress: number): boolean {
    const safeProgress = clampProgress(progress);
    return this.jumpRanges.some((jump) => {
      const captureEnd = Math.min(
        jump.gapEnd,
        jump.gapStart + LAUNCH_CAPTURE_DISTANCE / this.totalLength,
      );
      return safeProgress >= jump.launchStart && safeProgress <= captureEnd;
    });
  }

  /** True only where there is intentionally no road collision or rendered road. */
  isGapAt(progress: number): boolean {
    const safeProgress = clampProgress(progress);
    return this.jumpRanges.some(
      (jump) => safeProgress >= jump.gapStart && safeProgress < jump.gapEnd,
    );
  }

  /** Returns the launch owning a lip/gap sample, or null elsewhere. */
  getJumpRangeAt(progress: number): TrackJumpRange | null {
    const safeProgress = clampProgress(progress);
    return (
      this.jumpRanges.find(
        (jump) => safeProgress >= jump.launchStart && safeProgress <= jump.landingProgress,
      ) ?? null
    );
  }

  isTunnelAt(progress: number): boolean {
    const safeProgress = clampProgress(progress);
    return this.definition.tunnelFractions?.some((range) => inRange(safeProgress, range)) ?? false;
  }

  isBankedAt(progress: number): boolean {
    return Math.abs(this.getBankAt(progress)) > 0.01;
  }

  getBankAt(progress: number): number {
    const safeProgress = clampProgress(progress);
    let bank = this.interpolatePointNumber(safeProgress, 'bank', 0);
    for (const [start, end, amount] of this.definition.bankedFractions ?? []) {
      if (safeProgress < start || safeProgress > end) continue;
      const local = (safeProgress - start) / (end - start);
      const eased = Math.sin(local * Math.PI) ** 0.7;
      bank += amount * eased;
    }
    return bank;
  }

  private buildControlPointFractions(): readonly number[] {
    const distances = [0];
    let total = 0;
    for (let index = 1; index < this.definition.points.length; index += 1) {
      total += vectorFromPoint(this.definition.points[index]).distanceTo(
        vectorFromPoint(this.definition.points[index - 1]),
      );
      distances.push(total);
    }
    return Object.freeze(distances.map((distance) => (total > 0 ? distance / total : 0)));
  }

  private getAuthoredJumpProgresses(): number[] {
    if (this.definition.jumpFractions?.length) return [...this.definition.jumpFractions];
    return this.definition.points.flatMap((point, index) =>
      point.jump ? [this.controlPointFractions[index]] : [],
    );
  }

  private buildJumpRanges(): TrackJumpRange[] {
    const tuning = JUMP_TUNING_BY_THEME[this.definition.theme];
    return this.jumpProgresses.map((progress, index) => {
      const gapStart = clampProgress(progress);
      const gapEnd = clampProgress(gapStart + tuning.gapLength / this.totalLength);
      const landingProgress = clampProgress(gapEnd + tuning.landingBuffer / this.totalLength);
      const launchStart = clampProgress(gapStart - tuning.lipLength / this.totalLength);
      const launchY = this.curve.getPointAt(gapStart).y;
      const landingY = this.curve.getPointAt(landingProgress).y;
      return Object.freeze({
        id: `${this.definition.id}:jump:${index}`,
        index,
        progress: gapStart,
        launchStart,
        gapStart,
        gapEnd,
        landingProgress,
        lipLength: (gapStart - launchStart) * this.totalLength,
        gapLength: (gapEnd - gapStart) * this.totalLength,
        landingBuffer: (landingProgress - gapEnd) * this.totalLength,
        launchImpulse: tuning.launchImpulse,
        minimumLaunchSpeed: tuning.minimumLaunchSpeed,
        landingHeight: landingY,
        landingHeightDelta: landingY - launchY,
      });
    });
  }

  private controlSegment(progress: number): { left: number; right: number; alpha: number } {
    const safeProgress = clampProgress(progress);
    for (let index = 1; index < this.controlPointFractions.length; index += 1) {
      const rightFraction = this.controlPointFractions[index];
      if (safeProgress <= rightFraction) {
        const leftFraction = this.controlPointFractions[index - 1];
        const span = Math.max(EPSILON, rightFraction - leftFraction);
        return { left: index - 1, right: index, alpha: (safeProgress - leftFraction) / span };
      }
    }
    const last = this.definition.points.length - 1;
    return { left: last, right: last, alpha: 0 };
  }

  private interpolatePointNumber(
    progress: number,
    key: 'width' | 'bank',
    fallback: number,
  ): number {
    const segment = this.controlSegment(progress);
    const left = this.definition.points[segment.left][key] ?? fallback;
    const right = this.definition.points[segment.right][key] ?? fallback;
    return MathUtils.lerp(left, right, segment.alpha);
  }

  private nearestControlPointIndex(progress: number): number {
    const segment = this.controlSegment(progress);
    return segment.alpha < 0.5 ? segment.left : segment.right;
  }

  private nearestPointValue<K extends 'barriers'>(
    progress: number,
    key: K,
    fallback: BarrierState,
  ): BarrierState {
    const point = this.definition.points[this.nearestControlPointIndex(progress)];
    return point[key] ?? fallback;
  }

  private transformAt(progress: number, forwardOffset: number): TrackTransform {
    const safeProgress = clampProgress(progress + forwardOffset / this.totalLength);
    const sample = this.sampleAt(safeProgress);
    const yaw = Math.atan2(sample.forward.x, sample.forward.z);
    const pitch = Math.atan2(sample.forward.y, Math.hypot(sample.forward.x, sample.forward.z));
    return {
      progress: safeProgress,
      position: sample.position.clone().addScaledVector(sample.up, this.resetHeight),
      forward: sample.forward.clone(),
      right: sample.right.clone(),
      up: sample.up.clone(),
      yaw,
      pitch,
      roll: sample.bank,
    };
  }

  private createMarker(kind: TrackMarkerKind, progress: number, index: number): TrackMarker {
    const sample = this.sampleAt(progress);
    const resetTransform =
      kind === 'checkpoint' ? this.transformAt(progress, RESET_FORWARD_OFFSET) : this.transformAt(progress, 0);
    return {
      id: `${this.definition.id}:${kind}:${index}`,
      kind,
      index,
      progress,
      distance: progress * this.totalLength,
      position: sample.position.clone(),
      forward: sample.forward.clone(),
      right: sample.right.clone(),
      up: sample.up.clone(),
      width: sample.width,
      triggerRadius: kind === 'checkpoint' ? sample.width * 0.58 : Math.max(6, sample.width * 0.45),
      resetTransform: cloneTransform(resetTransform),
    };
  }

  private nearestCachedSegment(
    query: Vector3,
    startIndex: number,
    endIndex: number,
  ): { progress: number; distanceSq: number } {
    let bestProgress = startIndex / this.sampleCount;
    let bestDistanceSq = Infinity;
    const segment = new Vector3();
    const offset = new Vector3();
    const projected = new Vector3();

    for (let index = startIndex; index <= endIndex; index += 1) {
      const start = this.centerlineCache[index];
      const end = this.centerlineCache[index + 1];
      segment.subVectors(end, start);
      const lengthSq = segment.lengthSq();
      const alpha =
        lengthSq > EPSILON
          ? MathUtils.clamp(offset.subVectors(query, start).dot(segment) / lengthSq, 0, 1)
          : 0;
      projected.copy(start).addScaledVector(segment, alpha);
      const distanceSq = projected.distanceToSquared(query);
      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        bestProgress = (index + alpha) / this.sampleCount;
      }
    }

    return { progress: bestProgress, distanceSq: bestDistanceSq };
  }
}
